import { Card, Css, formatDate, FormLines, StaticField, useTestIds } from "@homebound/beam";
import { PLAN_FALLBACK_IMG } from "src/routes/libraries/plan-package/takeoffs/utils";
import { assertNever, formatList } from "src/utils";
import { SelectConfigurationStep_StructureFragment } from "../1.SelectConfigurationStep.generated";
import {
  ApplyConfigurationCard_AggregateVersionFragment,
  ApplyConfigurationCard_ProductOfferingFragment,
  ApplyConfigurationCard_ReadyPlanConfigLikeFragment,
} from "./ApplyConfigurationCard.generated";

type ApplyConfigurationCardProps = {
  structure: SelectConfigurationStep_StructureFragment;
  readyPlanConfig: ApplyConfigurationCard_ReadyPlanConfigLikeFragment;
  onClick?: () => void;
  onRemove?: () => void;
};

/** Displays a ready plan configuration's cover photo, plan and design selections. */
export function ApplyConfigurationCard(props: ApplyConfigurationCardProps) {
  const { structure, readyPlanConfig, onClick, onRemove } = props;
  const { name, updatedAt, readyPlan, exteriorOptionNames, interiorOptionNames } = readyPlanConfig;
  const { aggregateVersion, productOffering } = resolveConfigLikeFields(structure, readyPlanConfig);
  const tid = useTestIds({}, "applyConfigurationCard");
  const fields = [
    { label: "Plan", value: readyPlan?.name },
    { label: "Exterior", value: formatList(exteriorOptionNames) },
    { label: "Interior", value: formatList(interiorOptionNames) },
    { label: "Version", value: aggregateVersion?.displayVersion ?? "-" },
    { label: "Updated", value: formatDate(updatedAt.toPlainDate(), "shortDate") },
  ];
  return (
    <div onClick={onClick} css={Css.df.w100.$}>
      <Card
        bordered={true}
        fullWidth={true}
        imageFit="cover"
        imgSrc={productOffering?.coverAsset?.previewUrl ?? PLAN_FALLBACK_IMG}
        subtitle={readyPlan?.code ?? ""}
        title={name ?? "Current Configuration"}
        buttonMenuItems={onRemove ? [{ label: "Replace", icon: "trash", onClick: onRemove }] : undefined}
        detailContent={
          <div css={Css.df.fdc.gap1.$}>
            <FormLines width="sm" labelStyle="left" gap={0}>
              {fields.map((field) => (
                <StaticField key={field.label} label={field.label} value={field.value ?? "-"} />
              ))}
            </FormLines>
          </div>
        }
        {...tid}
      />
    </div>
  );
}

/**
 * Resolves the aggregateVersion and productOffering fields for a ready plan config like.
 * Since some config types conflicts on the same fields (e.g. Adu vs non-Adu), we need to resolve the correct fields based on the structure.
 */
function resolveConfigLikeFields(
  structure: SelectConfigurationStep_StructureFragment,
  readyPlanConfig: ApplyConfigurationCard_ReadyPlanConfigLikeFragment,
): {
  aggregateVersion: ApplyConfigurationCard_AggregateVersionFragment | undefined | null;
  productOffering: ApplyConfigurationCard_ProductOfferingFragment | undefined | null;
} {
  switch (readyPlanConfig.__typename) {
    case "CheckoutConfig":
      return {
        aggregateVersion: structure.isAdu ? readyPlanConfig.aduAggregateVersion : readyPlanConfig.aggregateVersion,
        productOffering: structure.isAdu ? readyPlanConfig.aduProductOffering : readyPlanConfig.productOffering,
      };
    case "ProjectReadyPlanConfig":
      return { aggregateVersion: readyPlanConfig.aggregateVersion, productOffering: readyPlanConfig.productOffering };
    case "ProductOfferingConfig":
      return {
        aggregateVersion: readyPlanConfig.pocProductOffering.aggregateCheckout,
        productOffering: readyPlanConfig.pocProductOffering,
      };
    default:
      assertNever(readyPlanConfig.__typename as never);
  }
}
